import {
  Component,
  computed,
  CUSTOM_ELEMENTS_SCHEMA,
  ElementRef,
  inject,
  input,
  InputSignal,
  viewChild,
} from '@angular/core';
import { SdDropdown } from '@solid-design-system/components/dist-versioned';
import { nextRender } from 'src/app/common/util/image-export';
import {
  TILE_EXPORT_FORMATS,
  TileExportFormat,
  TileExportHandler,
  TileExportRequest,
} from 'src/app/common/components/analysis/shared/tile/tile-export.handler';
import { AtomicComponent } from '../../shared/atomic/atomic.component';
import { TileBlueprintSupported } from '../../shared/tile/tile.component';
import { TranslocoModule } from '@ngneat/transloco';
import { AtomicBlueprint, Headline } from 'src/openapi-client';
import { FocusService } from 'src/app/common/services/focus.service';
import iconLibrary from 'src/app/common/iconLibrary';

@Component({
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  selector: 'app-default-blueprint',
  templateUrl: './default-blueprint.component.html',
  styleUrl: './default-blueprint.component.scss',
  imports: [AtomicComponent, TranslocoModule],
})
export class DefaultBlueprintComponent implements TileBlueprintSupported {
  elements = input.required<AtomicBlueprint[]>();
  tileId = input<string>('');
  readonly focus = inject(FocusService);
  readonly focused = computed(() => this.focus.focusedTileId() === this.tileId());
  protected readonly iconLibrary = iconLibrary;

  protected readonly focusLabel = computed(() =>
    this.focused() ? 'dashboard.tile.focus.exit' : 'dashboard.tile.focus.enter'
  );
  protected readonly focusIcon = computed(() => (this.focused() ? 'system/shrink' : 'system/enlarge'));

  private readonly exportHandler = inject(TileExportHandler, { optional: true });
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);
  protected readonly exportFormats = TILE_EXPORT_FORMATS;
  protected readonly exportAvailable = this.exportHandler !== null;

  private readonly tileMenu = viewChild<ElementRef<SdDropdown>>('tileMenu');

  protected async onExportClick(format: TileExportFormat): Promise<void> {
    const request = this.exportRequest();
    if (!request) {
      return;
    }
    await this.tileMenu()?.nativeElement.hide();
    await nextRender();
    await this.exportHandler?.exportTileAs(request, format);
  }

  private exportRequest(): TileExportRequest | undefined {
    const element = this.host.nativeElement.closest<HTMLElement>('app-tile') ?? this.host.nativeElement;
    const title = (this.headlines()[0] as Headline | undefined)?.text ?? '';
    return this.tileId() ? { tileId: this.tileId(), title, element } : undefined;
  }

  protected onFocusClick(event: Event): void {
    (event.currentTarget as HTMLElement).blur();
    this.focus.toggle(this.tileId());
  }

  headlines = computed(() => this.elements().filter(element => element.type == 'headline'));
  content = computed(() =>
    this.elements().filter(element => element.type != 'headline' && element.type != 'footnotes')
  );
  footnotes = computed(() => this.elements().filter(element => element.type == 'footnotes'));
}
